import { assign, createMachine } from 'xstate';
import type { EventFrom, MachineOptionsFrom, Sender } from 'xstate';
import { match } from 'ts-pattern';
import { CalendarDate } from '@internationalized/date';
import type { BackEvent, NextEvent } from '../navigation';
import { createCreateSalesContext } from './SalesMachine';
import { createCreateReleaseContext } from './ReleaseMachine';
import { StateFrom } from '../../utils/StateFrom';
import { GetEligibilityContextPromiseResult } from '../../promises/getEligibilityContextPromise';

import { Context } from './Context';

import { EligibilityState } from './state';
import { EligibilityStorage } from './storage';
import { EligibilityHistory } from './history';
import { logMachineError } from '../../utils/logError';

type ReplayableEvents = NextEvent | BackEvent;

type Events =
  | ReplayableEvents
  | { type: 'RESUME_RESIDENT' }
  | { type: 'RESUME_RELEASE' }
  | { type: 'RESUME_SALES' }
  | { type: 'RESUME_MASTER_RECORDING' }
  | { type: 'RESUME_SUMMARY' }
  | { type: 'RESTART' };

type Services = {
  getEligibilityContextPromise: {
    data: GetEligibilityContextPromiseResult;
  };
};

export const machine = createMachine(
  {
    predictableActionArguments: true,
    tsTypes: {} as import('./Machine.typegen').Typegen0,
    schema: {
      events: {} as Events,
      context: {} as Context,
      services: {} as Services,
    },
    id: 'eligibility',
    context: {
      releasesScannedDivisor: 1,
      tracksDownloadedDivisor: 1,
      tracksStreamedDivisor: 1,
      eligibilityCutoffDate: new CalendarDate(1990, 1, 1),
      genres: [],
      labels: [],
      distributors: [],
    },
    initial: 'init',
    invoke: {
      id: 'restart',
      src: 'restart',
    },
    states: {
      init: {
        invoke: {
          id: 'getEligibilityContextPromise',
          src: 'getEligibilityContextPromise',
          onDone: {
            actions: 'setContext',
            target: 'resume',
          },
          onError: 'error',
        },
      },
      error: { entry: 'logMachineError' },
      resume: {
        invoke: {
          id: 'resume',
          src: 'resume',
        },
        on: {
          RESUME_RESIDENT: 'resident',
          RESUME_RELEASE: 'release',
          RESUME_SALES: 'sales',
          RESUME_SUMMARY: 'summary',
          RESUME_MASTER_RECORDING: 'masterRecording',
        },
      },
      resident: {
        entry: 'saveResidentStep',
        invoke: {
          id: 'residentMachine',
          src: 'residentMachine',
        },
        on: {
          NEXT: 'release',
          BACK: { actions: 'close' },
        },
      },
      release: {
        entry: 'saveReleaseStep',
        invoke: {
          id: 'releaseMachine',
          src: 'releaseMachine',
          data: createCreateReleaseContext('eligibility'),
        },
        on: {
          NEXT: 'sales',
          BACK: 'resident',
        },
      },
      sales: {
        entry: 'saveSalesStep',
        invoke: {
          id: 'salesMachine',
          src: 'salesMachine',
          data: createCreateSalesContext('eligibility'),
        },
        on: {
          NEXT: 'masterRecording',
          BACK: 'release',
        },
      },
      masterRecording: {
        entry: 'saveMasterRecordingStep',
        invoke: {
          id: 'masterRecordingMachine',
          src: 'masterRecordingMachine',
        },
        on: {
          NEXT: 'summary',
          BACK: 'sales',
        },
      },
      summary: {
        entry: 'saveSummaryStep',
        invoke: {
          id: 'emailInvitationMachine',
          src: 'emailInvitationMachine',
        },
        on: {
          BACK: 'masterRecording',
        },
      },
    },
    on: { RESTART: 'init' },
  },
  {
    actions: { logMachineError, setContext: assign((_, { data }) => data) },
  }
);

type Machine = typeof machine;

export type EligibilityMachineState = StateFrom<Machine>;
export type EligibilityMachineSender = Sender<EventFrom<Machine>>;
export type EligibilityMachineOptions = MachineOptionsFrom<Machine, true>;

export const createResumeService =
  (storage: EligibilityStorage) => () => (send: EligibilityMachineSender) => {
    const { step } = storage.get();

    match(step)
      .with(undefined, () => send('RESUME_RESIDENT'))
      .with('resident', () => send('RESUME_RESIDENT'))
      .with('release', () => send('RESUME_RELEASE'))
      .with('sales', () => send('RESUME_SALES'))
      .with('masterRecording', () => send('RESUME_MASTER_RECORDING'))
      .with('summary', () => send('RESUME_SUMMARY'))
      .exhaustive();
  };

export const createRestartService =
  (storage: EligibilityStorage, history: EligibilityHistory) =>
  () =>
  (send: EligibilityMachineSender) => {
    history.subscribe((state) => {
      storage.set(state);
      send('RESTART');
    });

    return history.unsubscribe;
  };

const createStepSaver =
  (
    storage: EligibilityStorage,
    history: EligibilityHistory,
    step: EligibilityState['step']
  ) =>
  // Every time the user navigates forwards or backwards in the flow we
  // push an entry onto the history stack. This gives us a snapshot to
  // restore to if the user every decides to use the back and forward
  // buttons on their browser.
  (_: Context, { type }: Events) => {
    // We only want to push history when the user has been the one
    // triggering movement. There are other events used by the resume
    // serivce to restore the state of the flow but those are not
    // triggered by humans in front of the screen and therefore we do
    // not want to push history onto the stack for those events.
    if (type === 'NEXT' || type === 'BACK') {
      // The reason we first replace the current state is because the user
      // will have changed the state on the page since they first entered this
      // state. This allows us to save all their work up till they navigated
      // to the next step.
      history.replace(storage.get());
      storage.add({ step });
      history.push(storage.get());
    }
  };

export const createSaveActions = (
  storage: EligibilityStorage,
  history: EligibilityHistory
) => ({
  saveResidentStep: createStepSaver(storage, history, 'resident'),
  saveReleaseStep: createStepSaver(storage, history, 'release'),
  saveSalesStep: createStepSaver(storage, history, 'sales'),
  saveMasterRecordingStep: createStepSaver(storage, history, 'masterRecording'),
  saveSummaryStep: createStepSaver(storage, history, 'summary'),
});
